import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays } from 'date-fns';

import useMiniMwdChart from '@/apps/mwd-v2/components/MiniMwd/useMiniMwdChart';
import useWerklijnen from '@/apps/mwd-v2/hooks/useWerklijnen';
import useScrollStore from '@/apps/mwd-v2/state/selectors/useScrollStore';
import { resolveWerklijnIds } from '@/apps/mwd-v2/utils/resolveWerklijnIds';
import { searchCriteriaForEenheden, searchCriteriaForIds } from '@/components/search/v1/utils/helpers';
import useClientState from '@/state/useClientState';
import useSearchAndSidebarWerklijnen from '@/views/mwdView/useSearchAndSidebarWerklijnen';

import type { MiniMwdProps } from './miniMwdTypes';
import { buildLegendRows } from './miniMwdUtils';

type UseMiniMwdArgs = Pick<
  MiniMwdProps,
  'dialogData' | 'hideFetchToast' | 'eenheidNummers' | 'legendItems' | 'selectedLegendIds'
>;

export function useMiniMwd({
  dialogData,
  hideFetchToast,
  eenheidNummers,
  legendItems,
  selectedLegendIds,
}: UseMiniMwdArgs) {
  const { scrollEventHandler, miniMwdWidth, miniMwdHeight } = useMiniMwdChart();
  const { miniMwdScale } = useScrollStore();
  const selectDeeltraject = useClientState((state) => state.selectDeeltraject);
  const selectedItem = useClientState((state) => state.selected.selectedItems);
  const end = useClientState((state) => state.miniMwd.end);
  const mwdChartRef = useRef<HTMLDivElement>(null);
  const { miniWerklijnen } = useWerklijnen();
  const [miniScrollLeft, setMiniScrollLeft] = useState(0);
  const { handleSelectieUitPanel } = useSearchAndSidebarWerklijnen();

  const hasLegend = Boolean(legendItems?.length);
  const useEenhedenMode = Boolean(eenheidNummers?.length);

  // Track which werklijnIds / eenheidnummers we already fetched to avoid re-fetching in a loop
  const fetchedWerklijnIds = useRef<Set<string>>(new Set());
  const fetchedEenheidNummers = useRef<Set<string>>(new Set());
  // Track whether we've done the initial scroll + selection
  const hasInitializedSelection = useRef(false);

  // Resolve which werklijnen to render: prefer eenheidnummers, then dialogData, then the selected item.
  const selectedWerklijnen = useMemo(() => {
    if (useEenhedenMode) {
      return miniWerklijnen.filter((w) => eenheidNummers!.includes(w.eenheid?.nummer));
    }
    const werklijnIds = resolveWerklijnIds(dialogData, selectedItem[0]?.werklijnId);
    return werklijnIds.map((id) => miniWerklijnen.find((w) => w.werklijnId === id)).filter(Boolean);
  }, [useEenhedenMode, eenheidNummers, dialogData, selectedItem, miniWerklijnen]);

  const werklijnIdsToRender = useMemo(() => selectedWerklijnen.map((w) => w!.werklijnId), [selectedWerklijnen]);

  const firstInzet = dialogData?.[0]?.inzetId ?? selectedItem[0]?.selectedId;
  const firstWerklijn = selectedWerklijnen[0];
  const selectedInzet = firstWerklijn?.deelTrajecten.find((inzet) => inzet.wleId === firstInzet);

  const scrollTargetX = useMemo(() => {
    if (useEenhedenMode) {
      return miniMwdScale(new Date());
    }
    if (selectedInzet) {
      return miniMwdScale(selectedInzet.vertrekTijd);
    }
    return undefined;
  }, [miniMwdScale, selectedInzet, useEenhedenMode]);

  const scrollToInzet = useCallback(
    (x: number) => {
      const targetLeft = Math.max(0, x - 200);
      mwdChartRef.current?.scrollTo({ left: targetLeft, behavior: 'instant' });
      setMiniScrollLeft(targetLeft);
    },
    [setMiniScrollLeft],
  );

  useEffect(() => {
    if (!useEenhedenMode) return;
    const missing = eenheidNummers!.filter(
      (nr) => !miniWerklijnen.some((w) => w.eenheid?.nummer === nr) && !fetchedEenheidNummers.current.has(nr),
    );
    if (missing.length) {
      missing.forEach((nr) => fetchedEenheidNummers.current.add(nr));
      handleSelectieUitPanel([searchCriteriaForEenheden(missing)], { suppressToast: true });
    }
  }, [useEenhedenMode, eenheidNummers, miniWerklijnen, handleSelectieUitPanel]);

  useEffect(() => {
    if (useEenhedenMode) return;
    const referenced = resolveWerklijnIds(dialogData, selectedItem[0]?.werklijnId);
    const missing = referenced.filter(
      (id) => !miniWerklijnen.some((w) => w.werklijnId === id) && !fetchedWerklijnIds.current.has(id),
    );
    if (missing.length) {
      missing.forEach((id) => fetchedWerklijnIds.current.add(id));
      handleSelectieUitPanel([searchCriteriaForIds(missing)], { suppressToast: hideFetchToast });
    }
  }, [useEenhedenMode, dialogData, selectedItem, miniWerklijnen, hideFetchToast, handleSelectieUitPanel]);

  useEffect(() => {
    if (scrollTargetX !== undefined && !hasInitializedSelection.current) {
      hasInitializedSelection.current = true;
      scrollToInzet(scrollTargetX);
      if (!useEenhedenMode && dialogData?.[0]?.inzetId && selectedInzet) {
        selectDeeltraject(selectedInzet, false, false);
      }
    }
  }, [scrollTargetX, useEenhedenMode, selectedInzet, dialogData, scrollToInzet, selectDeeltraject]);

  const dayAfterTomorrow = useMemo(() => miniMwdScale(addDays(end, -1)), [miniMwdScale, end]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      setMiniScrollLeft(event.currentTarget.scrollLeft);
      scrollEventHandler(event);
    },
    [scrollEventHandler],
  );

  const totalWerklijnenHeight = selectedWerklijnen.reduce((sum, werklijn) => sum + (werklijn?.height ?? 0), 0);
  const svgHeight = totalWerklijnenHeight + 60 || miniMwdHeight;

  const legendRows = useMemo(
    () => buildLegendRows({ hasLegend, useEenhedenMode, selectedWerklijnen, legendItems, selectedLegendIds }),
    [hasLegend, useEenhedenMode, selectedWerklijnen, legendItems, selectedLegendIds],
  );

  return {
    mwdChartRef,
    miniScrollLeft,
    handleScroll,
    hasLegend,
    legendRows,
    werklijnIdsToRender,
    svgHeight,
    dayAfterTomorrow,
    miniMwdWidth,
    miniMwdScale,
  };
}
